/**
 * The page that hears the user. The native app cannot take a new library,
 * so the microphone is captured with Web APIs: in a WebView on iOS and
 * Android, in an iframe on the web. The page owns the speech socket (16 kHz
 * mono PCM up, captions down) and asks the app for a token whenever it has
 * to authenticate one; the app drives it by script. Which socket, and how
 * it speaks there, is its endpoint (`speechEndpoint`).
 */

import { SpeechEndpoint } from './speechEndpoint';

/** What the page tells the app. */
export type CaptureMessage =
  /** Needs a token for the socket; `refresh` when the last one was refused. */
  | { kind: 'authorize'; refresh: boolean }
  | { kind: 'started' }
  | { kind: 'partial'; text: string }
  /**
   * The sentence, with the token of its voice when the server parked it
   * (coaching practice keeps it with the line); null when none was.
   */
  | { kind: 'final'; text: string; voiceToken: string | null }
  | { kind: 'cancelled' }
  /**
   * What was said in the hold just let go, as a 16 kHz mono WAV (base64),
   * to be heard back; sent once, before its final sentence, by an endpoint
   * that asks for it.
   */
  | { kind: 'clip'; audio: string }
  /** Input loudness, 0 to 1, about ten times a second while held. */
  | { kind: 'level'; value: number }
  | { kind: 'error'; code: CaptureErrorCode };

export enum CaptureErrorCode {
  /** No microphone, or permission refused. */
  Microphone = 'microphone',
  /** The socket closed or could not authenticate. */
  Connection = 'connection',
  /** Out of AI credits. */
  Credits = 'credits',
  /** Another recognition of this user is still running. */
  Busy = 'busy',
  /** Held past the recognizer's 60 seconds. */
  TooLong = 'too_long',
  /** The store has no voice input (or the app no store). */
  FeatureNotEnabled = 'feature_not_enabled',
  /** The store's AI trial is used up. */
  TrialExhausted = 'trial_exhausted',
  /** Any other refusal or failure of the recognition. */
  Recognition = 'recognition',
}

const kMaxCaption = 2000;
const kVoiceToken = /^[0-9a-f-]{36}$/;
/** A hold's WAV, base64: 60 seconds (the recognizer's limit) and change. */
const kMaxClip = 2_700_000;

export function decodeCaptureMessage(
  text: string,
  session: string,
): CaptureMessage | null {
  if (text.length > kMaxClip + 200) return null;
  try {
    const value: unknown = JSON.parse(text);
    if (!value || typeof value !== 'object' || Array.isArray(value))
      return null;
    const message = value as Record<string, unknown>;
    if (message.session !== session) return null;
    switch (message.kind) {
      case 'authorize':
        return typeof message.refresh === 'boolean'
          ? { kind: 'authorize', refresh: message.refresh }
          : null;
      case 'started':
      case 'cancelled':
        return { kind: message.kind };
      case 'partial':
        return typeof message.text === 'string' &&
          message.text.length <= kMaxCaption
          ? { kind: 'partial', text: message.text }
          : null;
      case 'final':
        return typeof message.text === 'string' &&
          message.text.length <= kMaxCaption
          ? {
              kind: 'final',
              text: message.text,
              voiceToken:
                typeof message.voiceToken === 'string' &&
                kVoiceToken.test(message.voiceToken)
                  ? message.voiceToken
                  : null,
            }
          : null;
      case 'clip':
        return typeof message.audio === 'string' &&
          message.audio.length <= kMaxClip
          ? { kind: 'clip', audio: message.audio }
          : null;
      case 'level':
        return typeof message.value === 'number' &&
          message.value >= 0 &&
          message.value <= 1
          ? { kind: 'level', value: message.value }
          : null;
      case 'error':
        return Object.values(CaptureErrorCode).includes(
          message.code as CaptureErrorCode,
        )
          ? { kind: 'error', code: message.code as CaptureErrorCode }
          : null;
      default:
        return null;
    }
  } catch {
    return null;
  }
}

/** A hold's start frame payload, the endpoint's own (and the hold's number). */
export type SpeechStart = Record<string, string | boolean>;

/** What the page exposes as `window.speechCapture`. */
export interface SpeechCaptureApi {
  /** Starts a hold; the start frame carries `payload` and the hold's number. */
  start: (payload: SpeechStart) => void;
  stop: () => void;
  cancel: () => void;
  release: () => void;
  auth: (token: string | null) => void;
}

/** A script the app runs in the page. */
export const captureScripts = {
  start(payload: SpeechStart): string {
    const json = JSON.stringify(payload).replace(/</g, '\\u003c');
    return `window.speechCapture.start(${json});true;`;
  },
  stop: () => 'window.speechCapture.stop();true;',
  cancel: () => 'window.speechCapture.cancel();true;',
  /** Releases the microphone; the next start takes it again. */
  release: () => 'window.speechCapture.release();true;',
  auth(token: string | null): string {
    const json = JSON.stringify(token).replace(/</g, '\\u003c');
    return `window.speechCapture.auth(${json});true;`;
  },
};

export function buildCaptureHtml(
  endpoint: SpeechEndpoint,
  session: string,
): string {
  const socket = new URL(endpoint.socket);
  if (
    (socket.protocol !== 'wss:' && socket.protocol !== 'ws:') ||
    !/^[a-z]+$/.test(endpoint.frames) ||
    !Number.isSafeInteger(endpoint.keepMicrophoneMs) ||
    endpoint.keepMicrophoneMs < 0 ||
    !/^[a-zA-Z0-9-]+$/.test(session)
  )
    throw new Error('Invalid capture configuration');
  const json = (value: unknown) =>
    JSON.stringify(value).replace(/</g, '\\u003c');
  return `<!doctype html><html><head>
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta http-equiv="Content-Security-Policy" content="default-src 'none';
script-src 'nonce-${session}'; connect-src ${socket.origin};
base-uri 'none'; form-action 'none'">
</head><body>
<script nonce="${session}">
(()=>{
const SESSION=${json(session)};
const SOCKET=${json(socket.toString())};
/* The socket's frames: \`<prefix>.start\` and so on. */
const F=${json(endpoint.frames)}+'.';
/* Whether each hold's audio is handed back to be heard again. */
const CLIP=${json(endpoint.clip)};
const RATE=16000;
/* About 200 ms of audio a frame. */
const FRAME=3200;
/* A socket is replaced while idle once its token is this close to expiry. */
const EXPIRY_MARGIN=90000;
/* The microphone is let go after this long without a hold. */
const IDLE_RELEASE=${json(endpoint.keepMicrophoneMs)};
const KNOWN=['credits','busy','too_long','feature_not_enabled','trial_exhausted'];
const post=m=>{
  const text=JSON.stringify({session:SESSION,...m});
  if(window.ReactNativeWebView)window.ReactNativeWebView.postMessage(text);
  else window.parent.postMessage(text,'*');
};
/* The socket, whether it authenticated, and the one a token was asked for. */
let ws=null,ready=false,refreshed=false,outbox=[],asked=null,expiresAt=0;
let stream=null,ctx=null,node=null,source=null,opening=null,idleTimer=null;
/* The hold in progress: its number, and whether its final is awaited. */
let seq=0,live=false,awaiting=false;
let samples=[],tail=[],lastLevel=0,peak=0;
/* The hold's audio so far, kept to be heard back. */
let heard=[];
const settle=()=>{live=false;awaiting=false;scheduleRelease();};
const lose=code=>{
  const lost=live||awaiting;
  outbox=[];settle();
  if(lost)post({kind:'error',code});
};
const deliver=frame=>{
  if(ws&&ready)ws.send(JSON.stringify(frame));
  else{outbox.push(frame);connect();}
};
const drop=()=>{
  const old=ws;
  ws=null;ready=false;asked=null;
  if(old){try{old.close();}catch(e){}}
};
const connect=()=>{
  if(ws)return;
  ws=new WebSocket(SOCKET);
  const self=ws;
  self.onopen=()=>{asked=self;post({kind:'authorize',refresh:refreshed});};
  self.onclose=()=>{
    if(ws!==self)return;
    ws=null;ready=false;asked=null;
    lose('connection');
  };
  self.onmessage=event=>{
    if(ws!==self)return;
    let frame;
    try{frame=JSON.parse(event.data);}catch(e){return;}
    if(!frame||typeof frame.type!=='string')return;
    const data=frame.data||{};
    if(frame.type==='auth.ack'){
      if(data.ok){
        ready=true;refreshed=false;
        expiresAt=typeof data.tokenExp==='number'?data.tokenExp:0;
        const queued=outbox;outbox=[];
        for(const f of queued)self.send(JSON.stringify(f));
      }else if(!refreshed){
        refreshed=true;drop();
        if(outbox.length)connect();
      }else{
        refreshed=false;drop();lose('connection');
      }
      return;
    }
    /* Frames about an earlier hold are stale; a server that predates hold
       numbers sends none and is taken at its word. */
    if(data.seq!==undefined&&data.seq!==seq)return;
    switch(frame.type){
      case F+'started':if(live||awaiting)post({kind:'started'});break;
      case F+'partial':
        if(live||awaiting)post({kind:'partial',text:String(data.text||'')});
        break;
      case F+'final':
        if(!live&&!awaiting)break;
        /* Still listening: the server ended the hold on the speaker's
           silence, as asked; the microphone has nothing more to send. */
        if(live){samples=[];heard=[];}
        settle();
        post({kind:'final',text:String(data.text||''),
          voiceToken:typeof data.voiceToken==='string'?data.voiceToken:null});
        break;
      case F+'cancelled':post({kind:'cancelled'});break;
      case F+'error':
        if(!live&&!awaiting)break;
        settle();
        post({kind:'error',code:KNOWN.includes(data.code)?data.code:'recognition'});
        break;
    }
  };
};
const encode=bytes=>{
  let binary='';
  for(let i=0;i<bytes.length;i+=0x8000)
    binary+=String.fromCharCode.apply(null,bytes.subarray(i,i+0x8000));
  return btoa(binary);
};
const flush=()=>{
  if(!samples.length)return;
  const pcm=new Int16Array(samples);
  samples=[];
  if(live&&CLIP)heard.push(pcm);
  deliver({type:F+'audio',data:{pcm:encode(new Uint8Array(pcm.buffer))}});
};
/* Averages each step's input samples down to 16 kHz. The input samples a
   step did not reach yet open the next buffer's, so each is used once and
   no index ever falls outside the buffer. */
const capture=buffer=>{
  const step=ctx.sampleRate/RATE;
  const input=tail.length?[...tail,...buffer]:buffer;
  let i=0;
  for(;i+step<=input.length;i+=step){
    const from=Math.floor(i),to=Math.max(from+1,Math.floor(i+step));
    let sum=0;
    for(let k=from;k<to;k++)sum+=input[k];
    const s=Math.max(-1,Math.min(1,sum/(to-from)));
    peak=Math.max(peak,Math.abs(s));
    samples.push(s<0?s*0x8000:s*0x7fff);
  }
  tail=Array.prototype.slice.call(input,Math.floor(i));
  if(samples.length>=FRAME)flush();
  const now=Date.now();
  if(now-lastLevel>=100){
    lastLevel=now;
    post({kind:'level',value:Math.min(1,peak)});
    peak=0;
  }
};
/* The hold's audio as a 16-bit PCM WAV. */
const wav=chunks=>{
  let n=0;
  for(const c of chunks)n+=c.length;
  const v=new DataView(new ArrayBuffer(44+n*2));
  const tag=(at,s)=>{for(let i=0;i<4;i++)v.setUint8(at+i,s.charCodeAt(i));};
  tag(0,'RIFF');v.setUint32(4,36+n*2,true);tag(8,'WAVE');
  tag(12,'fmt ');v.setUint32(16,16,true);v.setUint16(20,1,true);
  v.setUint16(22,1,true);v.setUint32(24,RATE,true);v.setUint32(28,RATE*2,true);
  v.setUint16(32,2,true);v.setUint16(34,16,true);
  tag(36,'data');v.setUint32(40,n*2,true);
  let at=44;
  for(const c of chunks)for(let i=0;i<c.length;i++,at+=2)v.setInt16(at,c[i],true);
  return encode(new Uint8Array(v.buffer));
};
const releaseMicrophone=()=>{
  if(stream)stream.getTracks().forEach(t=>t.stop());
  if(ctx)ctx.close().catch(()=>{});
  stream=null;ctx=null;node=null;source=null;
};
const scheduleRelease=()=>{
  if(idleTimer)clearTimeout(idleTimer);
  idleTimer=setTimeout(()=>{if(!live&&!awaiting)releaseMicrophone();},IDLE_RELEASE);
};
/* A stream ended by a call or Siri, or a closed context, records silence. */
const healthy=()=>stream&&ctx&&ctx.state!=='closed'&&
  stream.getAudioTracks().some(t=>t.readyState==='live');
const microphone=()=>{
  if(healthy())return Promise.resolve();
  if(opening)return opening;
  releaseMicrophone();
  opening=(async()=>{
    try{
      const s=await navigator.mediaDevices.getUserMedia({audio:{
        channelCount:1,echoCancellation:true,noiseSuppression:true,
        autoGainControl:true}});
      const c=new (window.AudioContext||window.webkitAudioContext)();
      const src=c.createMediaStreamSource(s);
      const n=c.createScriptProcessor(4096,1,1);
      n.onaudioprocess=e=>{if(live)capture(e.inputBuffer.getChannelData(0));};
      src.connect(n);
      n.connect(c.destination);
      stream=s;ctx=c;source=src;node=n;
    }finally{opening=null;}
  })();
  return opening;
};
window.speechCapture={
  async start(payload){
    if(live)return;
    if(idleTimer)clearTimeout(idleTimer);
    /* Near its expiry the socket is replaced now, not cut mid-hold. */
    if(ws&&ready&&!awaiting&&expiresAt&&Date.now()>expiresAt-EXPIRY_MARGIN)
      drop();
    seq+=1;live=true;awaiting=false;samples=[];tail=[];heard=[];
    deliver({type:F+'start',data:{...payload,seq}});
    try{
      await microphone();
      if(ctx.state!=='running')await ctx.resume();
    }catch(e){
      const mine=live;
      live=false;awaiting=false;
      if(ready)deliver({type:F+'cancel',data:{}});
      else outbox=[];
      if(mine)post({kind:'error',code:'microphone'});
    }
  },
  stop(){
    if(!live)return;
    flush();
    live=false;awaiting=true;
    deliver({type:F+'stop',data:{}});
    if(heard.length)post({kind:'clip',audio:wav(heard)});
    heard=[];
  },
  cancel(){
    const held=live||awaiting;
    live=false;awaiting=false;samples=[];heard=[];
    scheduleRelease();
    /* Nothing reached the server yet: nothing to cancel there. */
    if(!ready){outbox=[];if(held)post({kind:'cancelled'});return;}
    deliver({type:F+'cancel',data:{}});
  },
  release(){
    live=false;awaiting=false;
    releaseMicrophone();
    drop();
  },
  auth(token){
    /* Only the socket that asked takes the token, and only once. */
    const target=asked;
    asked=null;
    if(!target||target!==ws)return;
    if(!token){drop();lose('connection');return;}
    target.send(JSON.stringify({type:'auth',data:{token}}));
  },
};
})();
</script></body></html>`;
}
